import styled from 'styled-components'

import ModalBase from 'LuxKit/Modal/ModalBase'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { addDays } from 'lib/datetime/dateUtils'
import { LUXURY_PLUS, LUXURY_PLUS_PATHS } from 'luxPlus/constants/base'

import LuxPlusFreePreviewUnlockedModalContent from './LuxPlusFreePreviewUnlockedModalContent'

// The branded card carries its own gradient, radius and shadow, so the modal sheet behind it is
// made invisible and just handles sizing/positioning within the portal.
const TransparentModal = styled(ModalBase)`
  && {
    background: transparent;
    box-shadow: none;
  }
`

interface Props {
  /** What enrolled the customer. Decides whether the preview is framed as a welcome gift. */
  trigger?: App.MemberSubscriptionItem['trigger']
}

/**
 * The connected "you've unlocked a free preview" modal, shown once a customer is enrolled into the
 * LuxPlus+ Free Preview (see useShowLuxPlusFreePreviewUnlockedModal). Replaces the old success
 * snackbar. Figma UX-4738.
 */
function LuxPlusFreePreviewUnlockedModal({ trigger }: Props) {
  const modalContext = useModalElementContext()
  const subscriptionExpiryDate = useAppSelector(state => state.auth.account.luxPlus.member.subscription.item?.expiryDate)

  const onClose = () => modalContext?.resolve()

  return (
    <TransparentModal size="S" dismissible>
      <LuxPlusFreePreviewUnlockedModalContent
        onClose={onClose}
        learnMoreTo={`/${LUXURY_PLUS_PATHS.DEFAULT_LANDING_PAGE}`}
        // Existing customers swept up by a campaign are not being welcomed — everyone else
        // (new registrations, manual joins) keeps the welcome-gift badge.
        showWelcomeGiftLabel={trigger !== 'auto-free-preview-enroll-for-existing-user'}
        previewEndDate={subscriptionExpiryDate ?? addDays(new Date(), LUXURY_PLUS.FREE_PREVIEW_DURATION).toISOString()}
      />
    </TransparentModal>
  )
}

export default LuxPlusFreePreviewUnlockedModal
